import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { settingsQueries, teamQueries } from '../../lib/queries';
import { T, sans, card, topBtn } from './VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';
import { guessSystemDomain } from './systemCatalog';

type Privilege = 'read' | 'write' | 'admin';
type Mfa = 'yes' | 'no' | 'unknown';
type Risk = 'high' | 'medium' | 'low';
type ReviewState = 'ok' | 'due_soon' | 'overdue' | 'not_needed';

interface HistoryEvent { id: string; kind: 'added' | 'updated' | 'reviewed' | 'revoked' | 'restored'; event_date: string | null; actor: string | null; note: string | null; created_at: string }

interface AccessEntry {
  id: string;
  system_name: string;
  system_domain: string | null;
  system_category: string | null;
  access_type: string;
  privilege: Privilege;
  sign_in_method: string | null;
  mfa: Mfa;
  account_count: number | null;
  accounts: string | null;
  data_access: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  approved_by: string | null;
  granted_on: string | null;
  expires_on: string | null;
  last_reviewed_on: string | null;
  last_reviewed_by: string | null;
  review_interval_months: number;
  status: 'active' | 'revoked';
  revoked_on: string | null;
  revoked_by: string | null;
  revocation_note: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  next_review_due: string | null;
  review_state: ReviewState;
  expiry_state: 'expired' | 'expiring' | 'ok' | null;
  risk_level: Risk | null;
  risk_factors: string[];
  history: HistoryEvent[];
}

interface Payload { entries: AccessEntry[]; vendor_lifecycle: string | null }

type View = 'active' | 'attention' | 'revoked' | 'all';
type SortKey = 'system' | 'risk' | 'privilege' | 'review' | 'expires';
type GroupBy = 'none' | 'category' | 'owner';

interface Props {
  vendorId: string;
  vendorName: string;
  isDark: boolean;
}

const ACCESS_TYPES: Record<string, string> = {
  user_accounts: 'User accounts', admin_account: 'Admin account', service_account: 'Service account', api_key: 'API key',
  integration: 'Integration', vpn: 'VPN / remote access', shared_account: 'Shared account', physical: 'Physical access', other: 'Other',
};
const CATEGORIES: Record<string, string> = {
  cloud_infrastructure: 'Cloud infrastructure', saas_application: 'SaaS application', internal_application: 'Internal application',
  network: 'Network & remote access', data_store: 'Database / data store', identity: 'Identity provider', endpoint: 'Endpoints & devices',
  physical: 'Physical site', other: 'Other',
};
const SIGN_IN: Record<string, string> = { sso: 'SSO', password: 'Password', api_key: 'API key', vpn: 'VPN', certificate: 'Certificate', other: 'Other' };
const PRIVILEGE_LABEL: Record<Privilege, string> = { read: 'Read', write: 'Write', admin: 'Admin' };
const PRIVILEGE_RANK: Record<Privilege, number> = { admin: 0, write: 1, read: 2 };
const RISK_LABEL: Record<Risk, string> = { high: 'High', medium: 'Medium', low: 'Low' };
const RISK_RANK: Record<Risk, number> = { high: 0, medium: 1, low: 2 };
const REVIEW_INTERVALS = [3, 6, 12, 24];
const HISTORY_LABEL: Record<HistoryEvent['kind'], string> = { added: 'Added', updated: 'Edited', reviewed: 'Reviewed — kept', revoked: 'Revoked', restored: 'Restored' };

const APP_HEADER_HEIGHT = 64;
const PANEL_SLIDE_MS = 220;

const accessKey = (vendorId: string) => ['vendors', 'detail', vendorId, 'access'] as const;
const apiError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || fallback;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  return fallback;
};
const parseDay = (iso: string | null) => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};
const fmtDay = (iso: string | null) => {
  const d = parseDay(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};
const daysUntil = (iso: string | null) => {
  const d = parseDay(iso);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
};
const inDays = (n: number) => (n === 0 ? 'today' : n > 0 ? `in ${n} day${n === 1 ? '' : 's'}` : `${-n} day${n === -1 ? '' : 's'} ago`);
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join('') || '?';
const csvCell = (value: unknown) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
// The logo comes from the website entered for the system, or from the catalog of well-known systems.
const logoDomain = (e: { system_name: string; system_domain: string | null }) => e.system_domain || guessSystemDomain(e.system_name) || '';
const systemSummary = (e: AccessEntry) => [e.system_category ? CATEGORIES[e.system_category] : null, ACCESS_TYPES[e.access_type] || e.access_type,
  e.account_count != null ? `${e.account_count} account${e.account_count === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');

const needsAttention = (e: AccessEntry) =>
  e.status === 'active' && (e.risk_level === 'high' || e.review_state === 'overdue' || e.review_state === 'due_soon' || e.expiry_state === 'expired' || e.expiry_state === 'expiring');

export default function VendorAccess({ vendorId, vendorName, isDark }: Props) {
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const canDelete = user?.role === 'admin';

  const c = {
    line: isDark ? '#1f1f1f' : T.bdr,
    ink: isDark ? '#e5e7eb' : T.ink,
    slate: isDark ? '#94a3b8' : T.slate,
    muted: isDark ? '#64748b' : T.muted,
    surface: isDark ? '#0d0d0d' : T.surface,
    surface2: isDark ? '#141414' : T.surface2,
    rowLine: isDark ? '#161616' : '#F1F5F9',
    accent: isDark ? '#60A5FA' : T.blue,
    accentBg: isDark ? '#0c1d33' : '#EFF6FF',
    red: isDark ? '#f87171' : '#DC2626',
    redBg: isDark ? '#2a1414' : '#FEF2F2',
    orange: isDark ? '#fb923c' : '#C2410C',
    orangeBg: isDark ? '#2a1a10' : '#FFF7ED',
    amber: isDark ? '#fbbf24' : '#B45309',
    amberBg: isDark ? '#2a2114' : '#FFFBEB',
    green: isDark ? '#4ade80' : '#15803D',
    greenBg: isDark ? '#132a1b' : '#F0FDF4',
    grayBg: isDark ? '#1a1a1a' : '#F1F5F9',
  };

  const [view, setView] = useState<View>('active');
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState<Risk | ''>('');
  const [privilegeFilter, setPrivilegeFilter] = useState<Privilege | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [noMfaOnly, setNoMfaOnly] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [sortKey, setSortKey] = useState<SortKey>('risk');
  const [sortAsc, setSortAsc] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [panelId, setPanelId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [formEntry, setFormEntry] = useState<AccessEntry | 'new' | null>(null);
  const [revoking, setRevoking] = useState<AccessEntry[] | null>(null);
  const [deleting, setDeleting] = useState<AccessEntry | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'ok' | 'error' } | null>(null);
  const panelTimer = useRef<number | undefined>(undefined);
  const viewChosen = useRef(false);

  const accessQuery = useQuery({
    queryKey: accessKey(vendorId),
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/access`)).data as Payload,
  });
  const membersQuery = useQuery({ ...teamQueries.members(), enabled: canEdit });
  const members = membersQuery.data ?? [];
  const data = accessQuery.data ?? null;
  const entries = useMemo(() => data?.entries ?? [], [data]);
  const offboarding = data?.vendor_lifecycle === 'offboarding';

  const active = entries.filter(e => e.status === 'active');
  const revoked = entries.filter(e => e.status === 'revoked');
  const attention = entries.filter(needsAttention);
  const highRisk = active.filter(e => e.risk_level === 'high').length;
  const mediumRisk = active.filter(e => e.risk_level === 'medium').length;
  const noMfa = active.filter(e => e.mfa === 'no').length;
  const unknownMfa = active.filter(e => e.mfa === 'unknown').length;
  const reviewsDue = active.filter(e => e.review_state === 'overdue' || e.review_state === 'due_soon');
  const overdue = active.filter(e => e.review_state === 'overdue').length;
  const expired = active.filter(e => e.expiry_state === 'expired').length;
  const expiring = active.filter(e => e.expiry_state === 'expiring').length;

  // Offboarding vendors open on what is still left to revoke; otherwise on what needs attention, if anything does.
  useEffect(() => {
    if (!data || viewChosen.current) return;
    viewChosen.current = true;
    setView(offboarding || !attention.length ? (active.length || !revoked.length ? 'active' : 'revoked') : 'attention');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const categories = useMemo(() => Array.from(new Set(entries.map(e => e.system_category).filter(Boolean) as string[])), [entries]);
  const owners = useMemo(() => Array.from(new Set(entries.map(e => e.owner_name).filter(Boolean) as string[])).sort(), [entries]);

  const query = search.trim().toLowerCase();
  const visible = useMemo(() => {
    const byView = view === 'active' ? active : view === 'revoked' ? revoked : view === 'attention' ? attention : entries;
    const dir = sortAsc ? 1 : -1;
    const riskRank = (e: AccessEntry) => (e.risk_level ? RISK_RANK[e.risk_level] : 9);
    const byName = (a: AccessEntry, b: AccessEntry) => a.system_name.localeCompare(b.system_name);
    const compare: Record<SortKey, (a: AccessEntry, b: AccessEntry) => number> = {
      system: (a, b) => dir * byName(a, b),
      risk: (a, b) => dir * (riskRank(a) - riskRank(b)) || PRIVILEGE_RANK[a.privilege] - PRIVILEGE_RANK[b.privilege] || byName(a, b),
      privilege: (a, b) => dir * (PRIVILEGE_RANK[a.privilege] - PRIVILEGE_RANK[b.privilege]) || byName(a, b),
      review: (a, b) => dir * ((a.next_review_due || '9999').localeCompare(b.next_review_due || '9999')) || byName(a, b),
      expires: (a, b) => dir * ((a.expires_on || '9999').localeCompare(b.expires_on || '9999')) || byName(a, b),
    };
    return byView
      .filter(e =>
        (!riskFilter || e.risk_level === riskFilter) &&
        (!privilegeFilter || e.privilege === privilegeFilter) &&
        (!categoryFilter || e.system_category === categoryFilter) &&
        (!ownerFilter || (ownerFilter === '__none' ? !e.owner_name : e.owner_name === ownerFilter)) &&
        (!noMfaOnly || e.mfa === 'no') &&
        (!query || [e.system_name, e.accounts, e.data_access, e.approved_by, e.owner_name, ACCESS_TYPES[e.access_type], e.system_category ? CATEGORIES[e.system_category] : '']
          .some(v => (v || '').toLowerCase().includes(query))))
      .sort((a, b) => Number(a.status === 'revoked') - Number(b.status === 'revoked') || compare[sortKey](a, b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, view, riskFilter, privilegeFilter, categoryFilter, ownerFilter, noMfaOnly, query, sortKey, sortAsc]);

  const panelEntry = entries.find(e => e.id === panelId) ?? null;
  const selectedEntries = entries.filter(e => selected.has(e.id) && e.status === 'active');

  // Drop selections that no longer exist or are no longer active (revoked elsewhere, filtered out of the data).
  useEffect(() => {
    setSelected(prev => {
      const keep = new Set(Array.from(prev).filter(id => entries.some(e => e.id === id && e.status === 'active')));
      return keep.size === prev.size ? prev : keep;
    });
  }, [entries]);

  const invalidates = { meta: { invalidates: [accessKey(vendorId)] } };
  const saveMutation = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id ? api.put(`/v1/vendors/${vendorId}/access/${id}`, body) : api.post(`/v1/vendors/${vendorId}/access`, body),
    ...invalidates,
  });
  const reviewMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => api.post(`/v1/vendors/${vendorId}/access/${id}/review`, { note: note || null }),
    ...invalidates,
  });
  const revokeMutation = useMutation({
    mutationFn: ({ id, revoked_on, note }: { id: string; revoked_on?: string; note?: string }) =>
      api.post(`/v1/vendors/${vendorId}/access/${id}/revoke`, { revoked_on: revoked_on || null, note: note || null }),
    ...invalidates,
  });
  const bulkMutation = useMutation({
    mutationFn: (body: { action: 'review' | 'revoke'; ids: string[]; on?: string; note?: string }) =>
      api.post(`/v1/vendors/${vendorId}/access/bulk`, { ...body, on: body.on || null, note: body.note || null }).then(r => r.data as { done: number; skipped: number }),
    ...invalidates,
  });
  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.post(`/v1/vendors/${vendorId}/access/${id}/restore`),
    ...invalidates,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/vendors/${vendorId}/access/${id}`),
    ...invalidates,
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => () => window.clearTimeout(panelTimer.current), []);

  const openPanel = (id: string) => { window.clearTimeout(panelTimer.current); setPanelId(id); setPanelVisible(true); };
  const closePanel = () => {
    setPanelVisible(false);
    window.clearTimeout(panelTimer.current);
    panelTimer.current = window.setTimeout(() => setPanelId(null), PANEL_SLIDE_MS);
  };
  const run = async (work: () => Promise<unknown>, success: string, failure: string) => {
    try { await work(); setToast({ message: success, tone: 'ok' }); return true; }
    catch (error) { setToast({ message: apiError(error, failure), tone: 'error' }); return false; }
  };
  const markReviewed = (e: AccessEntry) => run(() => reviewMutation.mutateAsync({ id: e.id }), `${e.system_name} reviewed and kept`, 'Could not record the review.');
  const restore = (e: AccessEntry) => run(() => restoreMutation.mutateAsync(e.id), `${e.system_name} is active again`, 'Could not restore the access.');
  const bulkReview = async () => {
    const ids = selectedEntries.map(e => e.id);
    try {
      const result = await bulkMutation.mutateAsync({ action: 'review', ids });
      setToast({ message: `${result.done} reviewed and kept${result.skipped ? ` · ${result.skipped} skipped` : ''}`, tone: 'ok' });
      setSelected(new Set());
    } catch (error) { setToast({ message: apiError(error, 'Could not record the reviews.'), tone: 'error' }); }
  };

  const toggleSort = (key: SortKey) => { if (sortKey === key) setSortAsc(!sortAsc); else { setSortKey(key); setSortAsc(true); } };
  const clearFilters = () => { setSearch(''); setRiskFilter(''); setPrivilegeFilter(''); setCategoryFilter(''); setOwnerFilter(''); setNoMfaOnly(false); };
  const filtersActive = !!(query || riskFilter || privilegeFilter || categoryFilter || ownerFilter || noMfaOnly);

  const exportCsv = () => {
    const header = ['System', 'Category', 'Access type', 'Risk', 'Risk factors', 'Privilege', 'Sign-in', 'MFA', 'Accounts', 'Account names',
      'Data it can reach', 'Owner', 'Approved by', 'Granted', 'Expires', 'Last reviewed', 'Reviewed by', 'Next review due', 'Status',
      'Revoked on', 'Revoked by', 'Revocation note', 'Notes'];
    const rows = visible.map(e => [
      e.system_name, e.system_category ? CATEGORIES[e.system_category] : '', ACCESS_TYPES[e.access_type] || e.access_type,
      e.risk_level ? RISK_LABEL[e.risk_level] : '', e.risk_factors.join('; '), PRIVILEGE_LABEL[e.privilege],
      e.sign_in_method ? SIGN_IN[e.sign_in_method] : '', e.mfa === 'unknown' ? 'Unknown' : e.mfa === 'yes' ? 'Yes' : 'No',
      e.account_count ?? '', e.accounts, e.data_access, e.owner_name, e.approved_by, e.granted_on, e.expires_on,
      e.last_reviewed_on, e.last_reviewed_by, e.next_review_due, e.status === 'active' ? 'Active' : 'Revoked',
      e.revoked_on, e.revoked_by, e.revocation_note, e.notes,
    ]);
    const csv = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slug(vendorName)}-access-inventory-${todayIso()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ── Loading / error ─────────────────────────────────────────── */

  if (!data && accessQuery.isPending) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ ...card(isDark), height: 84 }} />)}
        </div>
        <div style={{ ...card(isDark), height: 320 }} />
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ ...card(isDark), padding: 32, textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Couldn't load the access inventory</div>
        <div style={{ fontSize: 13, color: c.slate, marginBottom: 14 }}>{apiError(accessQuery.error, 'Something went wrong.')}</div>
        <button onClick={() => accessQuery.refetch()} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Try again</button>
      </div>
    );
  }

  /* ── Pieces ──────────────────────────────────────────────────── */

  const kpiLabel: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted };
  const kpiValue = (color: string): React.CSSProperties => ({ fontSize: 22, fontWeight: 700, color, marginTop: 6, lineHeight: 1 });
  const kpiNote: React.CSSProperties = { fontSize: 11, color: c.slate, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const kpi = (labelText: string, value: number, color: string, note: string, onClick?: () => void) => (
    <button onClick={onClick} disabled={!onClick} style={{ ...card(isDark), padding: '12px 14px', textAlign: 'left', fontFamily: sans, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={kpiLabel}>{labelText}</div>
      <div style={kpiValue(value ? color : c.ink)}>{value}</div>
      <div style={kpiNote}>{note}</div>
    </button>
  );
  const th: React.CSSProperties = {
    textAlign: 'left', fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted,
    padding: '9px 12px', background: c.surface2, borderBottom: `1px solid ${c.line}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { fontSize: 12, padding: '11px 12px', borderBottom: `1px solid ${c.rowLine}`, verticalAlign: 'middle', color: c.ink };
  const sortTh = (key: SortKey, label: string, width?: number) => (
    <th style={{ ...th, width, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(key)}>{label}{sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : ''}</th>
  );
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: c.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans };
  const control: React.CSSProperties = { fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, fontFamily: sans, cursor: 'pointer' };
  const pill = (text: string, color: string, bg: string) => (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: bg, color, whiteSpace: 'nowrap' }}>{text}</span>
  );
  const riskPill = (r: Risk | null) =>
    !r ? <span style={{ color: c.muted }}>—</span> : r === 'high' ? pill('High', c.red, c.redBg) : r === 'medium' ? pill('Medium', c.orange, c.orangeBg) : pill('Low', c.green, c.greenBg);
  const privilegePill = (p: Privilege) =>
    p === 'admin' ? pill('Admin', c.red, c.redBg) : p === 'write' ? pill('Write', c.amber, c.amberBg) : pill('Read', c.slate, c.grayBg);
  const mfaPill = (m: Mfa) =>
    m === 'yes' ? pill('MFA', c.green, c.greenBg) : m === 'no' ? pill('No MFA', c.red, c.redBg) : pill('MFA unknown', c.slate, c.grayBg);
  const ownerCell = (e: AccessEntry) => e.owner_name ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 22, height: 22, borderRadius: '50%', background: c.accentBg, color: c.accent, fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(e.owner_name)}</span>
      <span style={{ whiteSpace: 'nowrap' }}>{e.owner_name}</span>
    </span>
  ) : <span style={{ color: c.muted }}>Unassigned</span>;
  const reviewCell = (e: AccessEntry) => {
    if (e.status === 'revoked') return <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>Revoked {fmtDay(e.revoked_on)}</span>;
    const left = daysUntil(e.next_review_due);
    const lastText = e.last_reviewed_on ? `Last reviewed ${fmtDay(e.last_reviewed_on)}${e.last_reviewed_by ? ` by ${e.last_reviewed_by}` : ''}` : 'Never reviewed';
    return (
      <span title={lastText} style={{ color: e.review_state === 'overdue' ? c.red : e.review_state === 'due_soon' ? c.amber : c.ink, fontWeight: e.review_state === 'ok' ? 400 : 600, whiteSpace: 'nowrap' }}>
        {e.review_state === 'overdue' && left != null ? `Overdue ${-left} day${left === -1 ? '' : 's'}` : e.review_state === 'due_soon' && left != null ? `Due ${inDays(left)}` : `Due ${fmtDay(e.next_review_due)}`}
      </span>
    );
  };
  const expiresCell = (e: AccessEntry) => {
    if (!e.expires_on) return <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>No end date</span>;
    if (e.status === 'active' && e.expiry_state === 'expired') return <span style={{ color: c.red, fontWeight: 600, whiteSpace: 'nowrap' }}>Expired, still active</span>;
    const left = daysUntil(e.expires_on);
    const soon = e.status === 'active' && e.expiry_state === 'expiring';
    return (
      <span style={{ whiteSpace: 'nowrap', color: soon ? c.amber : c.ink, fontWeight: soon ? 600 : 400 }}>
        {fmtDay(e.expires_on)}{soon && left != null ? ` · ${inDays(left)}` : ''}
      </span>
    );
  };
  const viewTab = (key: View, label: string, count: number) => {
    const on = view === key;
    return (
      <button key={key} onClick={() => { setView(key); setSelected(new Set()); }} style={{
        background: 'none', border: 'none', borderBottom: `2px solid ${on ? c.ink : 'transparent'}`, marginBottom: -1,
        padding: '11px 12px', fontSize: 12, fontWeight: 600, color: on ? c.ink : c.slate, cursor: 'pointer', fontFamily: sans,
      }}>
        {label}
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: c.grayBg, color: c.slate, marginLeft: 6 }}>{count}</span>
      </button>
    );
  };
  const isActiveRow = (id: string) => panelVisible && panelId === id;
  const selectable = visible.filter(e => e.status === 'active');
  const allSelected = selectable.length > 0 && selectable.every(e => selected.has(e.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(e => e.id)));
  const toggleOne = (id: string) => setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const groupOf = (e: AccessEntry) =>
    groupBy === 'category' ? (e.system_category ? CATEGORIES[e.system_category] : 'No category') : groupBy === 'owner' ? (e.owner_name || 'Unassigned') : '';
  const columns = 8 + (canEdit ? 1 : 0);
  const rowFor = (e: AccessEntry) => (
    <tr key={e.id} data-access-row onClick={() => openPanel(e.id)}
      style={{ cursor: 'pointer', background: isActiveRow(e.id) ? c.accentBg : undefined }}
      onMouseEnter={ev => { if (!isActiveRow(e.id)) ev.currentTarget.style.background = c.surface2; }}
      onMouseLeave={ev => { ev.currentTarget.style.background = isActiveRow(e.id) ? c.accentBg : ''; }}>
      {canEdit && (
        <td style={{ ...td, width: 34, paddingRight: 0 }} onClick={ev => ev.stopPropagation()}>
          {e.status === 'active' && <input type="checkbox" aria-label={`Select ${e.system_name}`} checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} style={{ cursor: 'pointer' }} />}
        </td>
      )}
      <td style={td} title={systemSummary(e)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <VendorLogo key={logoDomain(e)} name={e.system_name} domain={logoDomain(e)} size={26} isDark={isDark} />
          <span style={{ fontSize: 13, fontWeight: 600, color: e.status === 'revoked' ? c.slate : c.ink, textDecoration: e.status === 'revoked' ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
            {e.system_name}
          </span>
        </div>
      </td>
      <td style={td} title={e.risk_factors.join(' · ') || undefined}>{riskPill(e.risk_level)}</td>
      <td style={td}>{privilegePill(e.privilege)}</td>
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ whiteSpace: 'nowrap' }}>{e.sign_in_method ? SIGN_IN[e.sign_in_method] : <span style={{ color: c.muted }}>—</span>}</span>
          {mfaPill(e.mfa)}
        </div>
      </td>
      <td style={td}>{ownerCell(e)}</td>
      <td style={td}>{reviewCell(e)}</td>
      <td style={td}>{expiresCell(e)}</td>
      {canEdit ? (
        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={ev => ev.stopPropagation()}>
          {e.status === 'active' ? (
            <>
              <button onClick={() => markReviewed(e)} disabled={reviewMutation.isPending} style={linkBtn}>Keep</button>
              <button onClick={() => setRevoking([e])} style={{ ...linkBtn, color: c.red, marginLeft: 12 }}>Revoke</button>
            </>
          ) : <button onClick={() => restore(e)} disabled={restoreMutation.isPending} style={linkBtn}>Restore</button>}
        </td>
      ) : <td style={td} />}
    </tr>
  );
  const renderRows = () => {
    if (groupBy === 'none') return visible.map(rowFor);
    const groups = new Map<string, AccessEntry[]>();
    visible.forEach(e => groups.set(groupOf(e), [...(groups.get(groupOf(e)) || []), e]));
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0])).flatMap(([name, rows]) => [
      <tr key={`group-${name}`}>
        <td colSpan={columns} style={{ ...td, background: isDark ? '#0a0a0a' : '#FBFCFE', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.slate, padding: '8px 12px' }}>
          {name} <span style={{ color: c.muted, fontWeight: 600 }}>· {rows.length}</span>
        </td>
      </tr>,
      ...rows.map(rowFor),
    ]);
  };

  const lastRevoked = revoked.map(e => e.revoked_on || '').sort().pop() || null;
  const showEmpty = entries.length === 0;

  return (
    <div style={{ fontFamily: sans }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: c.slate, maxWidth: 620, lineHeight: 1.5 }}>
          Every system, account and integration of yours that {vendorName} can get into, who owns it, and when it was last reviewed.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!showEmpty && <button onClick={exportCsv} disabled={!visible.length} style={{ ...topBtn(false, isDark), opacity: visible.length ? 1 : 0.5 }}>Export CSV</button>}
          {canEdit && active.length > 0 && <button onClick={() => setReviewing(true)} style={topBtn(false, isDark)}>Start access review</button>}
          {canEdit && <button onClick={() => setFormEntry('new')} style={topBtn(true, isDark)}>+ Add access</button>}
        </div>
      </div>

      {offboarding && entries.length > 0 && (
        <div style={{
          ...card(isDark), marginBottom: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          borderColor: active.length ? (isDark ? '#3a2a10' : '#FDE68A') : (isDark ? '#14361f' : '#BBF7D0'),
          background: active.length ? c.amberBg : c.greenBg,
        }}>
          <div style={{ flex: '1 1 280px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: active.length ? c.amber : c.green }}>
              {active.length ? 'Offboarding: revoke the remaining access' : 'Offboarding: all access revoked'}
            </div>
            <div style={{ fontSize: 12, color: c.slate, marginTop: 3 }}>
              {active.length
                ? `${revoked.length} of ${entries.length} revoked · ${active.length} still active.`
                : `All ${entries.length} revoked${lastRevoked ? `, the last on ${fmtDay(lastRevoked)}` : ''}. Export the list as evidence for your records.`}
            </div>
          </div>
          {canEdit && active.length > 0 && (
            <button onClick={() => setRevoking(active)} style={{ ...topBtn(true, isDark), background: '#DC2626' }}>Revoke all {active.length} remaining</button>
          )}
          {!active.length && <button onClick={exportCsv} style={topBtn(false, isDark)}>Export evidence</button>}
        </div>
      )}

      {showEmpty ? (
        <div style={{ ...card(isDark), padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Start the access inventory</div>
          <div style={{ fontSize: 12.5, color: c.slate, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
            Record every way {vendorName} can get into your systems: user or admin accounts, API keys, VPN, integrations or physical access.
            Each entry gets a risk level, an owner and a review date, and the list becomes your checklist when you offboard the vendor.
          </div>
          {canEdit && <button onClick={() => setFormEntry('new')} style={{ ...topBtn(true, isDark), margin: '16px auto 0' }}>+ Add access</button>}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))', gap: 10, marginBottom: 14 }}>
            {kpi('Active access', active.length, c.ink, revoked.length ? `${revoked.length} revoked` : 'None revoked', () => setView('active'))}
            {kpi('High risk', highRisk, c.red, mediumRisk ? `${mediumRisk} medium risk` : highRisk ? 'Admin, no MFA or overdue' : 'Nothing high risk', () => { setView('active'); setRiskFilter(highRisk ? 'high' : ''); })}
            {kpi('Without MFA', noMfa, c.red, unknownMfa ? `${unknownMfa} more not confirmed` : noMfa ? 'Password or key only' : 'MFA on all access', () => { setView('active'); setNoMfaOnly(noMfa > 0); })}
            {kpi('Reviews due', reviewsDue.length, overdue ? c.red : c.amber, overdue ? `${overdue} overdue` : reviewsDue.length ? 'Due within 30 days' : 'All reviews up to date', () => setView('attention'))}
            {kpi('Expiring', expired + expiring, expired ? c.red : c.amber, expired ? `${expired} past expiry, still active` : expiring ? 'Within 30 days' : 'No time-bound access ending', () => { setView('active'); setSortKey('expires'); setSortAsc(true); })}
          </div>

          <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${c.line}`, padding: '0 14px', flexWrap: 'wrap' }}>
              {viewTab('active', 'Active', active.length)}
              {viewTab('attention', 'Needs attention', attention.length)}
              {viewTab('revoked', 'Revoked', revoked.length)}
              {viewTab('all', 'All', entries.length)}
            </div>

            {selectedEntries.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderBottom: `1px solid ${c.line}`, background: c.accentBg, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: c.ink }}>{selectedEntries.length} selected</span>
                <button onClick={bulkReview} disabled={bulkMutation.isPending} style={linkBtn}>Mark reviewed — keep</button>
                <button onClick={() => setRevoking(selectedEntries)} style={{ ...linkBtn, color: c.red }}>Revoke</button>
                <button onClick={() => setSelected(new Set())} style={{ ...linkBtn, color: c.slate, marginLeft: 'auto' }}>Clear selection</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, padding: '12px 14px', borderBottom: `1px solid ${c.line}`, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 300 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.muted} strokeWidth="2" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                  </svg>
                  <input placeholder="Search systems, accounts, owners…" value={search} onChange={e => setSearch(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '7px 11px 7px 31px', fontSize: 12, fontFamily: sans, color: c.ink, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6, outline: 'none' }} />
                </div>
                <select value={riskFilter} onChange={e => setRiskFilter(e.target.value as Risk | '')} style={control}>
                  <option value="">All risk levels</option>
                  <option value="high">High risk</option>
                  <option value="medium">Medium risk</option>
                  <option value="low">Low risk</option>
                </select>
                <select value={privilegeFilter} onChange={e => setPrivilegeFilter(e.target.value as Privilege | '')} style={control}>
                  <option value="">All privileges</option>
                  <option value="admin">Admin</option>
                  <option value="write">Write</option>
                  <option value="read">Read</option>
                </select>
                {categories.length > 0 && (
                  <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={control}>
                    <option value="">All categories</option>
                    {categories.map(k => <option key={k} value={k}>{CATEGORIES[k]}</option>)}
                  </select>
                )}
                <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={control}>
                  <option value="">All owners</option>
                  {owners.map(o => <option key={o} value={o}>{o}</option>)}
                  <option value="__none">Unassigned</option>
                </select>
                <button onClick={() => setNoMfaOnly(!noMfaOnly)} style={{
                  fontSize: 11, fontWeight: 600, padding: '6px 11px', borderRadius: 99, cursor: 'pointer', fontFamily: sans,
                  border: `1px solid ${noMfaOnly ? c.ink : c.line}`, background: noMfaOnly ? c.ink : c.surface, color: noMfaOnly ? (isDark ? '#0d0d0d' : '#fff') : c.slate,
                }}>No MFA</button>
                <select value={groupBy} onChange={e => setGroupBy(e.target.value as GroupBy)} style={{ ...control, marginLeft: 'auto' }}>
                  <option value="none">Group: None</option>
                  <option value="category">Group: Category</option>
                  <option value="owner">Group: Owner</option>
                </select>
              </div>
            )}

            {visible.length === 0 ? (
              <div style={{ padding: '44px 24px', textAlign: 'center', fontSize: 13, color: c.slate }}>
                <div style={{ fontWeight: 600, color: c.ink, marginBottom: 4 }}>
                  {filtersActive ? 'No access matches these filters'
                    : view === 'attention' ? 'Nothing needs attention'
                      : view === 'revoked' ? 'Nothing revoked yet' : 'No active access'}
                </div>
                {filtersActive
                  ? <button onClick={clearFilters} style={linkBtn}>Clear filters</button>
                  : view === 'attention' && <div>No high-risk access, every review is up to date and nothing is about to expire.</div>}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {canEdit && (
                        <th style={{ ...th, width: 34, paddingRight: 0 }}>
                          {selectable.length > 0 && <input type="checkbox" aria-label="Select all active" checked={allSelected} onChange={toggleAll} style={{ cursor: 'pointer' }} />}
                        </th>
                      )}
                      {sortTh('system', 'System')}
                      {sortTh('risk', 'Risk', 120)}
                      {sortTh('privilege', 'Privilege', 90)}
                      <th style={th}>Sign-in</th>
                      <th style={th}>Owner</th>
                      {sortTh('review', 'Review', 140)}
                      {sortTh('expires', 'Expires', 130)}
                      <th style={{ ...th, width: canEdit ? 110 : 0 }} />
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
            )}
            {visible.length > 0 && <div style={{ padding: '11px 14px', fontSize: 11.5, color: c.slate }}>Showing {visible.length} of {entries.length}</div>}
          </div>
        </>
      )}

      {panelEntry && (
        <AccessPanel
          entry={panelEntry} isDark={isDark} c={c} visible={panelVisible} canEdit={canEdit} canDelete={canDelete}
          onClose={closePanel}
          onEdit={() => setFormEntry(panelEntry)}
          onReview={() => markReviewed(panelEntry)}
          onRevoke={() => setRevoking([panelEntry])}
          onRestore={() => restore(panelEntry)}
          onDelete={() => setDeleting(panelEntry)}
          riskPill={riskPill} privilegePill={privilegePill} mfaPill={mfaPill} pill={pill}
        />
      )}

      {formEntry && (
        <AccessForm
          entry={formEntry === 'new' ? null : formEntry} members={members}
          vendorName={vendorName} isDark={isDark} c={c} saving={saveMutation.isPending}
          onClose={() => setFormEntry(null)}
          onSave={async body => {
            const editing = formEntry !== 'new';
            try {
              await saveMutation.mutateAsync({ id: editing ? (formEntry as AccessEntry).id : null, body });
              setToast({ message: editing ? 'Access updated' : `${body.system_name} added`, tone: 'ok' });
              setFormEntry(null);
              return null;
            } catch (error) {
              return apiError(error, 'Could not save. Try again.');
            }
          }}
        />
      )}

      {revoking && (
        <RevokeDialog
          entries={revoking} isDark={isDark} c={c} saving={revokeMutation.isPending || bulkMutation.isPending}
          onClose={() => setRevoking(null)}
          onConfirm={async (revokedOn, note) => {
            const list = revoking;
            const ok = list.length === 1
              ? await run(() => revokeMutation.mutateAsync({ id: list[0].id, revoked_on: revokedOn, note }), `${list[0].system_name} revoked`, 'Could not revoke the access.')
              : await run(() => bulkMutation.mutateAsync({ action: 'revoke', ids: list.map(e => e.id), on: revokedOn, note }), `${list.length} accesses revoked`, 'Could not revoke the access.');
            if (ok) { setRevoking(null); setSelected(new Set()); }
          }}
        />
      )}

      {reviewing && (
        <ReviewSession
          entries={active} isDark={isDark} c={c}
          onClose={() => setReviewing(false)}
          onKeep={(e, note) => reviewMutation.mutateAsync({ id: e.id, note })}
          onRevoke={(e, note) => revokeMutation.mutateAsync({ id: e.id, note })}
          riskPill={riskPill} privilegePill={privilegePill} mfaPill={mfaPill}
        />
      )}

      {deleting && (
        <Modal c={c} title="Delete this entry?" onClose={() => setDeleting(null)} footer={
          <>
            <button onClick={() => setDeleting(null)} style={topBtn(false, isDark)}>Cancel</button>
            <button disabled={deleteMutation.isPending} onClick={async () => {
              const ok = await run(() => deleteMutation.mutateAsync(deleting.id), `${deleting.system_name} deleted`, 'Could not delete the entry.');
              if (ok) { setDeleting(null); closePanel(); }
            }} style={{ ...topBtn(true, isDark), background: '#DC2626', opacity: deleteMutation.isPending ? 0.7 : 1 }}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </button>
          </>
        }>
          <div style={{ fontSize: 13, color: c.ink, lineHeight: 1.6 }}>
            <strong>{deleting.system_name}</strong> and its history will be removed with no record left. Only do this for an entry added by mistake.
            If the vendor's access has ended, use <strong>Revoke</strong> instead so the revocation stays on record.
          </div>
        </Modal>
      )}

      {toast && createPortal(
        <div role="status" style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1400, padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans,
          background: c.surface, color: c.ink, border: `1px solid ${toast.tone === 'error' ? c.red : c.line}`,
          boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.8)' : '0 8px 24px rgba(15,23,42,.12)',
        }}>
          <span style={{ color: toast.tone === 'error' ? c.red : c.green, fontWeight: 700, marginRight: 8 }}>{toast.tone === 'error' ? '!' : '✓'}</span>{toast.message}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ─── Shared bits ─────────────────────────────────────────────────── */

type Palette = Record<string, string>;
type PillFn<V> = (value: V) => React.ReactNode;

function Modal({ title, subtitle, children, footer, onClose, c, width = 460 }: {
  title: string; subtitle?: string; children: React.ReactNode; footer: React.ReactNode; onClose: () => void; c: Palette; width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div data-access-modal onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: sans,
    }}>
      <div role="dialog" aria-label={title} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10, width, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: c.slate, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: c.muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto' }}>{children}</div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>{footer}</div>
      </div>
    </div>,
    document.body,
  );
}

const inputStyle = (c: Palette): React.CSSProperties => ({
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: c.ink,
  background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6, outline: 'none',
});

/* ─── Add / edit form ─────────────────────────────────────────────── */

function AccessForm({ entry, members, vendorName, isDark, c, saving, onClose, onSave }: {
  entry: AccessEntry | null; members: { id: string; name: string; email: string }[]; vendorName: string; isDark: boolean; c: Palette; saving: boolean;
  onClose: () => void; onSave: (body: Record<string, any>) => Promise<string | null>;
}) {
  // New grants start at the workspace's review interval (Settings -> Workflow defaults).
  const defaultsQuery = useQuery(settingsQueries.defaults());
  const defaultInterval = defaultsQuery.data?.values.access_review_months ?? 12;
  // Copied once when the form opens, so a background refetch never overwrites typing.
  const [f, setF] = useState(() => ({
    system_name: entry?.system_name ?? '',
    system_domain: entry?.system_domain ?? '',
    system_category: entry?.system_category ?? '',
    access_type: entry?.access_type ?? 'user_accounts',
    privilege: (entry?.privilege ?? 'read') as Privilege,
    sign_in_method: entry?.sign_in_method ?? '',
    mfa: (entry?.mfa ?? 'unknown') as Mfa,
    account_count: entry?.account_count != null ? String(entry.account_count) : '',
    accounts: entry?.accounts ?? '',
    data_access: entry?.data_access ?? '',
    owner_user_id: entry?.owner_user_id ?? '',
    approved_by: entry?.approved_by ?? '',
    granted_on: entry?.granted_on ?? '',
    expires_on: entry?.expires_on ?? '',
    last_reviewed_on: entry?.last_reviewed_on ?? '',
    review_interval_months: entry?.review_interval_months ?? defaultInterval,
    notes: entry?.notes ?? '',
  }));
  const [error, setError] = useState('');
  // The website follows the system name (from the catalog) until the user edits it themselves.
  const [domainTouched, setDomainTouched] = useState(!!entry?.system_domain);
  const set = (patch: Partial<typeof f>) => setF(prev => ({ ...prev, ...patch }));
  const setName = (name: string) => set(domainTouched ? { system_name: name } : { system_name: name, system_domain: guessSystemDomain(name) || '' });
  const input = inputStyle(c);
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: c.slate, marginBottom: 5 };
  const sectionTitle: React.CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted, margin: '4px 0 -2px' };
  const field = (label: string, el: React.ReactNode, hint?: string) => (
    <div><label style={labelStyle}>{label}</label>{el}{hint && <div style={{ fontSize: 11, color: c.muted, marginTop: 4 }}>{hint}</div>}</div>
  );
  const segmented = <V extends string>(value: V, options: [V, string][], onPick: (v: V) => void) => (
    <div style={{ display: 'flex', border: `1px solid ${c.line}`, borderRadius: 6, overflow: 'hidden' }}>
      {options.map(([v, label], i) => (
        <button key={v} type="button" onClick={() => onPick(v)} style={{
          flex: 1, padding: '7px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans, border: 'none',
          borderLeft: i ? `1px solid ${c.line}` : 'none', background: value === v ? c.ink : c.surface, color: value === v ? (isDark ? '#0d0d0d' : '#fff') : c.slate,
        }}>{label}</button>
      ))}
    </div>
  );
  const ownerOptions = Array.from(new Map([...members.map(m => [m.id, m.name || m.email] as const),
    ...(entry?.owner_user_id && entry.owner_name && !members.some(m => m.id === entry.owner_user_id) ? [[entry.owner_user_id, entry.owner_name] as const] : [])]).entries());

  const submit = async () => {
    if (!f.system_name.trim()) { setError('Enter the system the vendor can access.'); return; }
    if (f.account_count && Number(f.account_count) > 100000) { setError('Number of accounts is too large.'); return; }
    if (f.last_reviewed_on && f.last_reviewed_on > todayIso()) { setError("The last review date can't be in the future."); return; }
    if (f.granted_on && f.last_reviewed_on && f.last_reviewed_on < f.granted_on) { setError("The last review can't be before the access was granted."); return; }
    if (f.granted_on && f.expires_on && f.expires_on < f.granted_on) { setError("The expiry date can't be before the access was granted."); return; }
    setError('');
    const problem = await onSave({
      system_name: f.system_name.trim(), system_domain: f.system_domain.trim() || null, system_category: f.system_category || null, access_type: f.access_type, privilege: f.privilege,
      sign_in_method: f.sign_in_method || null, mfa: f.mfa, account_count: f.account_count ? Number(f.account_count) : null,
      accounts: f.accounts, data_access: f.data_access, owner_user_id: f.owner_user_id || null, approved_by: f.approved_by,
      granted_on: f.granted_on || null, expires_on: f.expires_on || null, last_reviewed_on: f.last_reviewed_on || null,
      review_interval_months: f.review_interval_months, notes: f.notes,
    });
    if (problem) setError(problem);
  };

  return (
    <Modal c={c} width={600} onClose={onClose}
      title={entry ? `Edit access: ${entry.system_name}` : 'Add access'}
      subtitle={`A system, account or integration of yours that ${vendorName} can get into.`}
      footer={
        <>
          {error && <span style={{ flex: 1, fontSize: 12, color: c.red }}>{error}</span>}
          <button onClick={onClose} disabled={saving} style={topBtn(false, isDark)}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...topBtn(true, isDark), opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : entry ? 'Save changes' : 'Add access'}</button>
        </>
      }>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={sectionTitle}>System</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
          {field('System *', (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <VendorLogo key={f.system_domain} name={f.system_name || '?'} domain={f.system_domain.trim()} size={34} isDark={isDark} />
              <input autoFocus style={input} value={f.system_name} maxLength={200} placeholder="Salesforce, AWS production, Office VPN" onChange={e => setName(e.target.value)} />
            </div>
          ))}
          {field('Website', <input style={input} value={f.system_domain} maxLength={253} placeholder="salesforce.com" onChange={e => { setDomainTouched(true); set({ system_domain: e.target.value }); }} />,
            'Used for the logo. Filled in for well-known systems.')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('Category', (
            <select style={input} value={f.system_category} onChange={e => set({ system_category: e.target.value })}>
              <option value="">Not set</option>
              {Object.entries(CATEGORIES).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          ))}
          {field('Data it can reach', <input style={input} value={f.data_access} maxLength={1000} placeholder="Customer records, payment logs" onChange={e => set({ data_access: e.target.value })} />)}
        </div>

        <div style={sectionTitle}>Access</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('Access type', (
            <select style={input} value={f.access_type} onChange={e => set({ access_type: e.target.value })}>
              {Object.entries(ACCESS_TYPES).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          ))}
          {field('Privilege', segmented<Privilege>(f.privilege, [['read', 'Read'], ['write', 'Write'], ['admin', 'Admin']], v => set({ privilege: v })))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('How they sign in', (
            <select style={input} value={f.sign_in_method} onChange={e => set({ sign_in_method: e.target.value })}>
              <option value="">Not recorded</option>
              {Object.entries(SIGN_IN).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          ))}
          {field('MFA enforced', segmented<Mfa>(f.mfa, [['yes', 'Yes'], ['no', 'No'], ['unknown', 'Unknown']], v => set({ mfa: v })))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
          {field('Accounts', <input style={input} inputMode="numeric" value={f.account_count} placeholder="3" onChange={e => set({ account_count: e.target.value.replace(/[^\d]/g, '') })} />)}
          {field('Account names', <input style={input} value={f.accounts} maxLength={1000} placeholder="jane@vendor.com, svc-vendor-sync" onChange={e => set({ accounts: e.target.value })} />)}
        </div>

        <div style={sectionTitle}>Ownership and review</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('Owner on your side', (
            <select style={input} value={f.owner_user_id} onChange={e => set({ owner_user_id: e.target.value })}>
              <option value="">Unassigned</option>
              {ownerOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          ), 'Accountable for this access and its reviews.')}
          {field('Approved by', <input style={input} value={f.approved_by} maxLength={200} placeholder="Name of the approver" onChange={e => set({ approved_by: e.target.value })} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('Granted on', <input type="date" style={input} value={f.granted_on} onChange={e => set({ granted_on: e.target.value })} />)}
          {field('Expires on', <input type="date" style={input} value={f.expires_on} onChange={e => set({ expires_on: e.target.value })} />, 'For time-bound access. Leave empty if it has no end date.')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('Last reviewed on', <input type="date" style={input} max={todayIso()} value={f.last_reviewed_on} onChange={e => set({ last_reviewed_on: e.target.value })} />)}
          {field('Review every', (
            <select style={input} value={f.review_interval_months} onChange={e => set({ review_interval_months: Number(e.target.value) })}>
              {Array.from(new Set([...REVIEW_INTERVALS, f.review_interval_months])).sort((a, b) => a - b).map(m => <option key={m} value={m}>{m} months</option>)}
            </select>
          ), 'Auditors usually expect 6 or 12 months.')}
        </div>
        {field('Notes', <textarea rows={2} style={{ ...input, resize: 'vertical' }} value={f.notes} maxLength={2000} onChange={e => set({ notes: e.target.value })} />)}
      </div>
    </Modal>
  );
}

/* ─── Revoke dialog (one or many) ─────────────────────────────────── */

function RevokeDialog({ entries, isDark, c, saving, onClose, onConfirm }: {
  entries: AccessEntry[]; isDark: boolean; c: Palette; saving: boolean; onClose: () => void; onConfirm: (revokedOn: string, note: string) => void;
}) {
  const [revokedOn, setRevokedOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const input = inputStyle(c);
  const many = entries.length > 1;
  return (
    <Modal c={c} onClose={onClose} title={many ? `Revoke ${entries.length} accesses` : `Revoke access: ${entries[0].system_name}`}
      subtitle="Record that the access has been removed. It stays on the list as revoked, as evidence."
      footer={
        <>
          <button onClick={onClose} disabled={saving} style={topBtn(false, isDark)}>Cancel</button>
          <button onClick={() => onConfirm(revokedOn, note.trim())} disabled={saving || !revokedOn} style={{ ...topBtn(true, isDark), background: '#DC2626', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Revoking…' : many ? `Revoke ${entries.length}` : 'Revoke access'}
          </button>
        </>
      }>
      <div style={{ display: 'grid', gap: 14 }}>
        {many && (
          <div style={{ fontSize: 12.5, color: c.ink, lineHeight: 1.6, maxHeight: 120, overflowY: 'auto' }}>
            {entries.map(e => e.system_name).join(', ')}
          </div>
        )}
        <div>
          <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: c.slate, marginBottom: 5 }}>Revoked on</label>
          <input type="date" style={input} max={todayIso()} value={revokedOn} onChange={e => setRevokedOn(e.target.value)} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: c.slate, marginBottom: 5 }}>Note (optional)</label>
          <textarea rows={3} style={{ ...input, resize: 'vertical' }} maxLength={1000} value={note} placeholder="Accounts disabled in Okta, API key rotated" onChange={e => setNote(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/* ─── Guided access review ────────────────────────────────────────── */

function ReviewSession({ entries, isDark, c, onClose, onKeep, onRevoke, riskPill, privilegePill, mfaPill }: {
  entries: AccessEntry[]; isDark: boolean; c: Palette; onClose: () => void;
  onKeep: (e: AccessEntry, note: string) => Promise<unknown>; onRevoke: (e: AccessEntry, note: string) => Promise<unknown>;
  riskPill: PillFn<Risk | null>; privilegePill: PillFn<Privilege>; mfaPill: PillFn<Mfa>;
}) {
  const due = entries.filter(e => e.review_state === 'overdue' || e.review_state === 'due_soon');
  // The list is fixed when the review starts, so refetches during the review don't reshuffle it.
  const [queue, setQueue] = useState<AccessEntry[] | null>(null);
  const [index, setIndex] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tally, setTally] = useState({ kept: 0, revoked: 0, skipped: 0 });
  const input = inputStyle(c);

  const start = (list: AccessEntry[]) => {
    const order = { high: 0, medium: 1, low: 2 } as Record<string, number>;
    setQueue([...list].sort((a, b) => (order[a.risk_level || 'low'] - order[b.risk_level || 'low']) || a.system_name.localeCompare(b.system_name)));
  };
  const decide = async (decision: 'keep' | 'revoke' | 'skip') => {
    if (!queue) return;
    const e = queue[index];
    setError('');
    if (decision !== 'skip') {
      setBusy(true);
      try { await (decision === 'keep' ? onKeep(e, note.trim()) : onRevoke(e, note.trim())); }
      catch (err) { setError(apiError(err, 'Could not save this decision.')); setBusy(false); return; }
      setBusy(false);
    }
    setTally(t => ({ ...t, [decision === 'keep' ? 'kept' : decision === 'revoke' ? 'revoked' : 'skipped']: t[decision === 'keep' ? 'kept' : decision === 'revoke' ? 'revoked' : 'skipped'] + 1 }));
    setNote('');
    setIndex(i => i + 1);
  };

  const footerDone = <button onClick={onClose} style={topBtn(true, isDark)}>Done</button>;

  if (!queue) {
    const option = (title: string, text: string, list: AccessEntry[]) => (
      <button disabled={!list.length} onClick={() => start(list)} style={{
        textAlign: 'left', padding: '14px 16px', borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, cursor: list.length ? 'pointer' : 'not-allowed',
        opacity: list.length ? 1 : 0.5, fontFamily: sans, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: c.ink }}>{title}</span>
          <span style={{ display: 'block', fontSize: 12, color: c.slate, marginTop: 2 }}>{text}</span>
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: c.ink }}>{list.length}</span>
      </button>
    );
    return (
      <Modal c={c} width={520} onClose={onClose} title="Start an access review"
        subtitle="Go through each access one by one and decide whether the vendor should keep it. Each decision is saved as you go."
        footer={<button onClick={onClose} style={topBtn(false, isDark)}>Cancel</button>}>
        <div style={{ display: 'grid', gap: 10 }}>
          {option('Due for review', 'Overdue, or due within 30 days.', due)}
          {option('All active access', 'A full review of everything the vendor can reach.', entries)}
        </div>
      </Modal>
    );
  }

  if (index >= queue.length) {
    return (
      <Modal c={c} width={520} onClose={onClose} title="Access review complete" footer={footerDone}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[['Kept', tally.kept, c.green], ['Revoked', tally.revoked, c.red], ['Skipped', tally.skipped, c.slate]].map(([label, value, color]) => (
            <div key={label as string} style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${c.line}` }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: value ? (color as string) : c.ink, marginTop: 6 }}>{value as number}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: c.slate, marginTop: 14, lineHeight: 1.6 }}>
          Each decision is recorded with today's date and your name in the access's history, and in the vendor's audit log.
          {tally.skipped ? ' Skipped entries keep their current review date.' : ''}
        </div>
      </Modal>
    );
  }

  const e = queue[index];
  const kvRow = (k: string, v: React.ReactNode) => (
    <>
      <span style={{ color: c.slate }}>{k}</span>
      <span style={{ color: v ? c.ink : c.muted, wordBreak: 'break-word' }}>{v || 'Not recorded'}</span>
    </>
  );
  return (
    <Modal c={c} width={560} onClose={onClose} title={`Access review · ${index + 1} of ${queue.length}`}
      subtitle="Should the vendor keep this access?"
      footer={
        <>
          {error && <span style={{ flex: 1, fontSize: 12, color: c.red }}>{error}</span>}
          <button onClick={() => decide('skip')} disabled={busy} style={{ ...topBtn(false, isDark), marginRight: 'auto' }}>Skip</button>
          <button onClick={() => decide('revoke')} disabled={busy} style={{ ...topBtn(false, isDark), color: c.red }}>Revoke</button>
          <button onClick={() => decide('keep')} disabled={busy} style={{ ...topBtn(true, isDark), opacity: busy ? 0.7 : 1 }}>{busy ? 'Saving…' : 'Keep access'}</button>
        </>
      }>
      <div style={{ height: 4, borderRadius: 99, background: c.grayBg, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ width: `${(index / queue.length) * 100}%`, height: '100%', background: c.accent, transition: 'width .2s' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{riskPill(e.risk_level)}{privilegePill(e.privilege)}{mfaPill(e.mfa)}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <VendorLogo key={logoDomain(e)} name={e.system_name} domain={logoDomain(e)} size={40} isDark={isDark} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: c.ink }}>{e.system_name}</div>
          <div style={{ fontSize: 12, color: c.slate, marginTop: 2 }}>{systemSummary(e)}</div>
        </div>
      </div>
      {e.risk_factors.length > 0 && (
        <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 7, background: e.risk_level === 'high' ? c.redBg : c.orangeBg, color: e.risk_level === 'high' ? c.red : c.orange, fontSize: 12.5 }}>
          {e.risk_factors.join(' · ')}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '7px 10px', fontSize: 12.5, marginTop: 14 }}>
        {kvRow('Accounts', [e.account_count != null ? `${e.account_count}` : null, e.accounts].filter(Boolean).join(' · '))}
        {kvRow('Data it can reach', e.data_access)}
        {kvRow('Owner', e.owner_name)}
        {kvRow('Last reviewed', e.last_reviewed_on ? `${fmtDay(e.last_reviewed_on)}${e.last_reviewed_by ? ` by ${e.last_reviewed_by}` : ''}` : 'Never')}
        {kvRow('Expires', e.expires_on ? fmtDay(e.expires_on) : 'No end date')}
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: c.slate, marginBottom: 5 }}>Note for this decision (optional)</label>
        <input style={input} value={note} maxLength={1000} placeholder="Still needed for monthly billing sync" onChange={ev => setNote(ev.target.value)} />
      </div>
    </Modal>
  );
}

/* ─── Detail drawer ───────────────────────────────────────────────── */

function AccessPanel({ entry: e, isDark, c, visible, canEdit, canDelete, onClose, onEdit, onReview, onRevoke, onRestore, onDelete, riskPill, privilegePill, mfaPill, pill }: {
  entry: AccessEntry; isDark: boolean; c: Palette; visible: boolean; canEdit: boolean; canDelete: boolean;
  onClose: () => void; onEdit: () => void; onReview: () => void; onRevoke: () => void; onRestore: () => void; onDelete: () => void;
  riskPill: PillFn<Risk | null>; privilegePill: PillFn<Privilege>; mfaPill: PillFn<Mfa>; pill: (text: string, color: string, bg: string) => React.ReactNode;
}) {
  const panelRef = useRef<HTMLElement>(null);

  // Outside clicks close the drawer; clicks on another row switch to it, and dialogs opened from here keep it open.
  useEffect(() => {
    const onDown = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest('[data-access-row], [data-access-modal]')) return;
      onClose();
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape' && !document.querySelector('[data-access-modal]')) onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const label: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted };
  const section: React.CSSProperties = { padding: '16px 20px', borderBottom: `1px solid ${c.line}` };
  const kv: React.CSSProperties = { display: 'grid', gridTemplateColumns: '130px 1fr', gap: '8px 10px', fontSize: 12.5, marginTop: 10 };
  const btn: React.CSSProperties = {
    fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '8px 12px', borderRadius: 6, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, cursor: 'pointer',
  };
  const row = (k: string, v: React.ReactNode) => (
    <>
      <span style={{ color: c.slate }}>{k}</span>
      <span style={{ color: v ? c.ink : c.muted, wordBreak: 'break-word' }}>{v || 'Not recorded'}</span>
    </>
  );
  const left = daysUntil(e.next_review_due);
  const history: HistoryEvent[] = e.history.length ? e.history
    : [{ id: 'created', kind: 'added', event_date: e.created_at?.slice(0, 10) ?? null, actor: e.created_by, note: null, created_at: e.created_at }];
  const dotColor = (k: HistoryEvent['kind']) => (k === 'revoked' ? c.red : k === 'reviewed' ? c.green : k === 'restored' ? c.accent : c.muted);

  return createPortal(
    <aside ref={panelRef} data-access-panel role="dialog" aria-label={`${e.system_name} details`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(460px, 100vw)', zIndex: 1150, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${visible ? 'accessPanelIn' : 'accessPanelOut'} ${PANEL_SLIDE_MS}ms ${visible ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      <style>{`
        @keyframes accessPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes accessPanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { aside[data-access-panel] { animation-duration: 1ms !important; } }
      `}</style>

      <div style={section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {e.status === 'active' ? riskPill(e.risk_level) : pill('Revoked', c.slate, c.grayBg)}
            {privilegePill(e.privilege)}
            {mfaPill(e.mfa)}
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: c.muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <VendorLogo key={logoDomain(e)} name={e.system_name} domain={logoDomain(e)} size={40} isDark={isDark} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.ink, wordBreak: 'break-word' }}>{e.system_name}</div>
            <div style={{ fontSize: 12, color: c.slate, marginTop: 2 }}>{systemSummary(e)}</div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {e.status === 'active' && e.risk_factors.length > 0 && (
          <div style={section}>
            <div style={label}>Why it's {e.risk_level ? RISK_LABEL[e.risk_level].toLowerCase() : ''} risk</div>
            <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
              {e.risk_factors.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.ink }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: e.risk_level === 'high' ? c.red : c.orange, flexShrink: 0 }} />{f}
                </div>
              ))}
            </div>
          </div>
        )}

        {e.status === 'revoked' && (
          <div style={{ ...section, background: c.grayBg }}>
            <div style={label}>Revoked</div>
            <div style={kv}>
              {row('Revoked on', fmtDay(e.revoked_on))}
              {row('Revoked by', e.revoked_by)}
              {e.revocation_note && row('Note', e.revocation_note)}
            </div>
          </div>
        )}

        <div style={section}>
          <div style={label}>Access</div>
          <div style={kv}>
            {row('Website', e.system_domain)}
            {row('Accounts', e.account_count != null ? `${e.account_count}` : null)}
            {row('Account names', e.accounts)}
            {row('Sign-in', e.sign_in_method ? SIGN_IN[e.sign_in_method] : null)}
            {row('MFA', e.mfa === 'yes' ? 'Enforced' : e.mfa === 'no' ? 'Not enforced' : 'Unknown')}
            {row('Data it can reach', e.data_access)}
          </div>
        </div>

        <div style={section}>
          <div style={label}>Ownership and review</div>
          <div style={kv}>
            {row('Owner', e.owner_name || 'Unassigned')}
            {row('Approved by', e.approved_by)}
            {row('Granted on', e.granted_on ? fmtDay(e.granted_on) : null)}
            {row('Expires', e.expires_on ? (
              <span style={{ color: e.status === 'active' && e.expiry_state === 'expired' ? c.red : e.status === 'active' && e.expiry_state === 'expiring' ? c.amber : c.ink }}>
                {fmtDay(e.expires_on)}{e.status === 'active' && e.expiry_state === 'expired' ? ' · expired, still active' : ''}
              </span>
            ) : 'No end date')}
            {row('Last reviewed', e.last_reviewed_on ? `${fmtDay(e.last_reviewed_on)}${e.last_reviewed_by ? ` by ${e.last_reviewed_by}` : ''}` : 'Never')}
            {row('Review every', `${e.review_interval_months} months`)}
            {e.status === 'active' && row('Next review', (
              <span style={{ color: e.review_state === 'overdue' ? c.red : e.review_state === 'due_soon' ? c.amber : c.ink, fontWeight: e.review_state === 'ok' ? 400 : 600 }}>
                {fmtDay(e.next_review_due)}{e.review_state !== 'ok' && left != null ? ` · ${left < 0 ? `overdue by ${-left} day${left === -1 ? '' : 's'}` : inDays(left)}` : ''}
              </span>
            ))}
          </div>
        </div>

        {e.notes && (
          <div style={section}>
            <div style={label}>Notes</div>
            <p style={{ fontSize: 12.5, lineHeight: 1.6, color: c.ink, margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{e.notes}</p>
          </div>
        )}

        <div style={{ ...section, borderBottom: 'none' }}>
          <div style={label}>History</div>
          <div style={{ position: 'relative', marginTop: 12 }}>
            <div style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 1, background: c.line }} />
            {history.map(h => (
              <div key={h.id} style={{ display: 'flex', gap: 12, padding: '5px 0', position: 'relative' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(h.kind), border: `2px solid ${c.surface}`, flexShrink: 0, marginTop: 4, zIndex: 1 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: c.ink }}>{HISTORY_LABEL[h.kind]}</span>
                    <span style={{ fontSize: 11, color: c.muted, flexShrink: 0 }}>{fmtDay(h.event_date)}</span>
                  </div>
                  {h.note && <div style={{ fontSize: 12, color: c.slate, marginTop: 1, wordBreak: 'break-word' }}>{h.note}</div>}
                  {h.actor && <div style={{ fontSize: 11, color: c.muted, marginTop: 1 }}>by {h.actor}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {canEdit && (
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${c.line}`, display: 'grid', gap: 8 }}>
          {e.status === 'active' ? (
            <>
              <button onClick={onReview} style={{ ...btn, background: c.ink, color: isDark ? '#0d0d0d' : '#fff', border: `1px solid ${c.ink}`, padding: 9 }}>Mark reviewed — keep access</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onEdit} style={{ ...btn, flex: 1 }}>Edit</button>
                <button onClick={onRevoke} style={{ ...btn, flex: 1, color: c.red }}>Revoke</button>
                {canDelete && <button onClick={onDelete} style={{ ...btn, flex: 1, color: c.muted }}>Delete</button>}
              </div>
            </>
          ) : (
            <>
              <button onClick={onRestore} style={{ ...btn, background: c.ink, color: isDark ? '#0d0d0d' : '#fff', border: `1px solid ${c.ink}`, padding: 9 }}>Restore as active</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onEdit} style={{ ...btn, flex: 1 }}>Edit</button>
                {canDelete && <button onClick={onDelete} style={{ ...btn, flex: 1, color: c.muted }}>Delete</button>}
              </div>
            </>
          )}
        </div>
      )}
    </aside>,
    document.body,
  );
}
